import { CurrentUserStocksQuery, LastEventType, Sma } from "../generated/graphql";

type StockForRecommendation = Pick<CurrentUserStocksQuery['currentUser']['stocks'][number], 'signals' | 'stock'>;

export type Recommendation = 'ACHETER' | 'CONSERVER' | 'VENDRE' | 'ATTENDRE';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type SignalMethod = 'rsi' | 'sma';

export const formatRecommendation = (
  stock: StockForRecommendation,
  method: SignalMethod,
  now = new Date(),
): Recommendation => {
  const lastEvent = stock.signals?.[method]?.lastEvent;
  if (!lastEvent) return 'ATTENDRE';

  if (lastEvent.type === LastEventType.Sell) return 'VENDRE';

  const signalAgeMs = now.getTime() - new Date(lastEvent.date).getTime();
  const currentClose = stock.stock.historical2?.edges[0]?.node?.close;

  if (
    signalAgeMs < 2 * MS_PER_DAY &&
    currentClose != null &&
    Math.abs(currentClose - lastEvent.buyValue) / lastEvent.buyValue < 0.01
  ) {
    return 'ACHETER';
  }

  return 'CONSERVER';
};

/**
 * RSI and SMA agreeing on ACHETER/VENDRE is a stronger signal than either alone;
 * CONSERVER/ATTENDRE agreement carries no actionable meaning and is never highlighted.
 */
export const isConsensusRecommendation = (
  rsi: Recommendation,
  sma: Recommendation,
): boolean => rsi === sma && (rsi === 'ACHETER' || rsi === 'VENDRE');

export const formatLastSignalValue = (
  stock: Pick<CurrentUserStocksQuery['currentUser']['stocks'][number], 'signals'>,
): string => {
  const lastEvent = stock.signals?.rsi?.lastEvent;
  if (!lastEvent) return '-';

  const value = lastEvent.type === LastEventType.Buy ? lastEvent.buyValue : lastEvent.sellValue;
  if (value == null) return '-';

  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Formats the last signal date from a stock
 */
export const formatLastSignalDate = (
  stock: Pick<CurrentUserStocksQuery['currentUser']['stocks'][number], 'signals'>
): string => {
  const date = stock.signals?.rsi?.lastEvent?.date;
  if (!date) return "-";

  // Format the date - you can customize this based on your requirements
  return new Date(date).toLocaleDateString("fr-FR");
};

export type LastComputedDate = { label: 'RSI' | 'SMA'; date: string };

/**
 * Formats the last computation date per followed signal (rsi and/or sma).
 * Distinct from formatLastSignalDate: this reflects when the calculation last ran,
 * not when a BUY/SELL event was last triggered.
 */
export const formatLastComputedDates = (
  stock: Pick<CurrentUserStocksQuery['currentUser']['stocks'][number], 'signals'>,
): LastComputedDate[] => {
  const dates: LastComputedDate[] = [];
  const rsiComputedAt = stock.signals?.rsi?.lastComputedAt;
  if (rsiComputedAt) {
    dates.push({ label: 'RSI', date: new Date(rsiComputedAt).toLocaleDateString('fr-FR') });
  }
  const smaComputedAt = stock.signals?.sma?.lastComputedAt;
  if (smaComputedAt) {
    dates.push({ label: 'SMA', date: new Date(smaComputedAt).toLocaleDateString('fr-FR') });
  }
  return dates;
};

const SMA_LABELS: Record<Sma, string> = {
  [Sma.Sma_20]: '20 jours',
  [Sma.Sma_50]: '50 jours',
  [Sma.Sma_100]: '100 jours',
};

export const formatSmaLabel = (usedSma: Sma): string => SMA_LABELS[usedSma] ?? '-';

export const formatGain = (
  stock: Pick<CurrentUserStocksQuery['currentUser']['stocks'][number], 'signals'>,
  currentPrice?: number | null,
): string => {
  const lastEvent = stock.signals?.rsi?.lastEvent;
  if (!lastEvent) return '—';

  if (lastEvent.type === LastEventType.Buy) {
    if (currentPrice == null) return '—';
    const percentGain = (currentPrice - lastEvent.buyValue) / lastEvent.buyValue;
    return new Intl.NumberFormat('fr-FR', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      signDisplay: 'always',
    }).format(percentGain);
  }

  // Série fermée (SELL) : aucune position ouverte, pas de gain à afficher
  return '—';
};

export const formatCurrentPrice = (
  stock: Pick<CurrentUserStocksQuery['currentUser']['stocks'][number], 'stock'>
): string => {
  const closeValue = stock.stock.historical2?.edges[0]?.node?.close;
  if (closeValue == null) return '-';
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(closeValue);
};

/**
 * Formats the trend information from a stock as a percentage
 */
export const formatTrend = (
  stock: Pick<CurrentUserStocksQuery['currentUser']['stocks'][number], 'stock'>
): string => {
  const closeValue = stock.stock.historical2?.edges[0]?.node?.close;
  const dayBeforeCloseValue = stock.stock.historical2?.edges[1]?.node?.close;

  if (closeValue === undefined || closeValue === null ||
      dayBeforeCloseValue === undefined || dayBeforeCloseValue === null ||
      dayBeforeCloseValue === 0) {
    return "-";
  }

  // Calculate percentage change
  const percentChange = ((closeValue - dayBeforeCloseValue) / dayBeforeCloseValue) * 100;

  // Format as percentage with 2 decimal places
  return new Intl.NumberFormat('fr-FR', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay: 'always'
  }).format(percentChange / 100);
};
